"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createCarbonViewForBuilding = exports.createCarbonViewForTotal = exports.createCarbonViewSummaryFromValueGroups = exports.createCostingViewForBuilding = exports.createCostingViewForTotalWithoutScenario = exports.createCostingViewForTotal = exports.createCostingViewSummaryFromValueGroups = void 0;
const number_1 = require("@project-rouge/service-core/data/number");
const value_breakdown_1 = require("../data/value-breakdown");
const flatten_value_group_1 = require("./value-breakdown/flatten-value-group");
const sum_value_group_1 = require("./value-breakdown/sum-value-group");
const value_group_1 = require("./value-group");
/**
 * Creates a custom view of the costing summary data.
 */
const createCostingViewSummaryFromValueGroups = (groups) => {
    const constructionModules = (0, value_breakdown_1.getValueGroupItemByIndex)(groups, "1.1" /* CostSummaryGroupItemIndex.ConstructionModules */)?.value ?? 0;
    const constructionFitOut = (0, value_breakdown_1.getValueGroupItemByIndex)(groups, "1.2" /* CostSummaryGroupItemIndex.ConstructionFitOut */)?.value ?? 0;
    const constructionBaseBuild = (0, value_breakdown_1.getValueGroupItemByIndex)(groups, "1.3" /* CostSummaryGroupItemIndex.ConstructionBaseBuild */)?.value ?? 0;
    const construction = (0, number_1.toPrecision)(constructionModules + constructionFitOut + constructionBaseBuild, 2);
    const preliminaries = (0, value_breakdown_1.getValueGroupItemByIndex)(groups, "2.1" /* CostSummaryGroupItemIndex.Preliminaries */)?.value ?? 0;
    const contingencies = (0, value_breakdown_1.getValueGroupItemByIndex)(groups, "3.1" /* CostSummaryGroupItemIndex.Contingency */)?.value ?? 0;
    const overheadsAndProfits = (0, value_breakdown_1.getValueGroupItemByIndex)(groups, "4.1" /* CostSummaryGroupItemIndex.OverheadsAndProfit */)?.value ?? 0;
    return {
        construction,
        constructionModules,
        constructionFitOut,
        constructionBaseBuild,
        preliminaries,
        contingencies,
        overheadsAndProfits,
        total: (0, number_1.toPrecision)(construction + preliminaries + contingencies + overheadsAndProfits, 2),
    };
};
exports.createCostingViewSummaryFromValueGroups = createCostingViewSummaryFromValueGroups;
/**
 * Creates a custom view of the costing data for totals including buildings and scenario.
 */
const createCostingViewForTotal = (calculation) => {
    const summary = [];
    (0, sum_value_group_1.mergeValueGroupIntoOutput)(calculation.scenario.costing.summary, summary);
    (0, sum_value_group_1.mergeValueGroupIntoOutput)((0, flatten_value_group_1.flatternValueGroupsForIndex)(calculation.buildings.costing.modular.summary, 0), summary);
    (0, sum_value_group_1.mergeValueGroupIntoOutput)((0, flatten_value_group_1.flatternValueGroupsForIndex)(calculation.buildings.costing.basebuild.summary, 0), summary);
    const basebuild = [];
    (0, sum_value_group_1.mergeValueGroupIntoOutput)(calculation.scenario.costing.element, basebuild);
    (0, sum_value_group_1.mergeValueGroupIntoOutput)((0, flatten_value_group_1.flatternValueGroupsForIndex)(calculation.buildings.costing.basebuild.element, 0), basebuild);
    return {
        summary: (0, exports.createCostingViewSummaryFromValueGroups)(summary),
        element: {
            modular: (0, value_group_1.recreateWithTotalForValueGroup)((0, flatten_value_group_1.flatternValueGroupsForIndex)(calculation.buildings.costing.modular.element, 0)),
            basebuild: (0, value_group_1.recreateWithTotalForValueGroup)(basebuild),
        },
    };
};
exports.createCostingViewForTotal = createCostingViewForTotal;
/**
 * Creates a custom view of the costing data for totals including buildings.
 * This view is without the scenario data added.
 */
const createCostingViewForTotalWithoutScenario = (calculation) => {
    const summary = [];
    (0, sum_value_group_1.mergeValueGroupIntoOutput)((0, flatten_value_group_1.flatternValueGroupsForIndex)(calculation.buildings.costing.modular.summary, 0), summary);
    (0, sum_value_group_1.mergeValueGroupIntoOutput)((0, flatten_value_group_1.flatternValueGroupsForIndex)(calculation.buildings.costing.basebuild.summary, 0), summary);
    return {
        summary: (0, exports.createCostingViewSummaryFromValueGroups)(summary),
        element: {
            modular: (0, value_group_1.recreateWithTotalForValueGroup)((0, flatten_value_group_1.flatternValueGroupsForIndex)(calculation.buildings.costing.modular.element, 0)),
            basebuild: (0, value_group_1.recreateWithTotalForValueGroup)((0, flatten_value_group_1.flatternValueGroupsForIndex)(calculation.buildings.costing.basebuild.element, 0)),
        },
    };
};
exports.createCostingViewForTotalWithoutScenario = createCostingViewForTotalWithoutScenario;
/**
 * Creates a custom view of the costing data for totals for a single building.
 * The {@link building} index should be zero based and will be adjusted to look in the correct aggregated location.
 * Naturally the building values will not include any scenario costing values.
 */
const createCostingViewForBuilding = (calculation, building) => {
    // Moving the index along one position as the first value is always the total.
    const index = building + 1;
    const summary = [];
    (0, sum_value_group_1.mergeValueGroupIntoOutput)((0, flatten_value_group_1.flatternValueGroupsForIndex)(calculation.buildings.costing.modular.summary, index), summary);
    (0, sum_value_group_1.mergeValueGroupIntoOutput)((0, flatten_value_group_1.flatternValueGroupsForIndex)(calculation.buildings.costing.basebuild.summary, index), summary);
    return {
        summary: (0, exports.createCostingViewSummaryFromValueGroups)(summary),
        element: {
            modular: (0, value_group_1.recreateWithTotalForValueGroup)((0, flatten_value_group_1.flatternValueGroupsForIndex)(calculation.buildings.costing.modular.element, index)),
            basebuild: (0, value_group_1.recreateWithTotalForValueGroup)((0, flatten_value_group_1.flatternValueGroupsForIndex)(calculation.buildings.costing.basebuild.element, index)),
        },
    };
};
exports.createCostingViewForBuilding = createCostingViewForBuilding;
/**
 * Creates a custom view of the carbon summary data.
 */
const createCarbonViewSummaryFromValueGroups = (groups) => {
    const substructure = (0, value_breakdown_1.getValueGroupByIndex)(groups, "1" /* CostElementReferenceGroupIndex.Substructure */)?.total ?? 0;
    const superstructure = (0, value_breakdown_1.getValueGroupByIndex)(groups, "2" /* CostElementReferenceGroupIndex.Superstructure */)?.total ?? 0;
    const otherInternalFinishes = (0, value_breakdown_1.getValueGroupByIndex)(groups, "3" /* CostElementReferenceGroupIndex.InternalFinishes */)?.total ?? 0;
    const otherFittingsAndFurniture = (0, value_breakdown_1.getValueGroupByIndex)(groups, "4" /* CostElementReferenceGroupIndex.FittingsFurnitureAndEquipment */)?.total ?? 0;
    const otherServices = (0, value_breakdown_1.getValueGroupByIndex)(groups, "5" /* CostElementReferenceGroupIndex.Services */)?.total ?? 0;
    const other = (0, number_1.toPrecision)(otherInternalFinishes + otherFittingsAndFurniture + otherServices, 2);
    return {
        substructure,
        superstructure,
        other,
        total: (0, number_1.toPrecision)(substructure + superstructure + other, 2),
    };
};
exports.createCarbonViewSummaryFromValueGroups = createCarbonViewSummaryFromValueGroups;
/**
 * Creates a custom view of the carbon data for totals including buildings and scenario.
 */
const createCarbonViewForTotal = (calculation) => {
    const element = [];
    (0, sum_value_group_1.mergeValueGroupIntoOutput)(calculation.scenario.carbon, element);
    (0, sum_value_group_1.mergeValueGroupIntoOutput)((0, flatten_value_group_1.flatternValueGroupsForIndex)(calculation.buildings.carbon, 0), element);
    return {
        summary: (0, exports.createCarbonViewSummaryFromValueGroups)(element),
        element: (0, value_group_1.recreateWithTotalForValueGroup)(element),
    };
};
exports.createCarbonViewForTotal = createCarbonViewForTotal;
/**
 * Creates a custom view of the carbon data for totals for a single building.
 * The {@link building} index should be zero based and will be adjusted to look in the correct aggregated location.
 * Naturally the building values will not include any scenario carbon values.
 */
const createCarbonViewForBuilding = (calculation, building) => {
    // Moving the index along one position as the first value is always the total.
    const index = building + 1;
    const element = (0, flatten_value_group_1.flatternValueGroupsForIndex)(calculation.buildings.carbon, index);
    return {
        summary: (0, exports.createCarbonViewSummaryFromValueGroups)(element),
        element: (0, value_group_1.recreateWithTotalForValueGroup)(element),
    };
};
exports.createCarbonViewForBuilding = createCarbonViewForBuilding;
